import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import SEOHead from './SEOHead';
import ReconstitutionCalculator from './ReconstitutionCalculator';
import DosageCalculator from './DosageCalculator';
import SyringeDisplay from './SyringeDisplay';
import { SYRINGE_OPTIONS, DEFAULT_SYRINGE_TYPE } from '../constants/syringeTypes';
import '../App.css';
import './PeptideCalculatorTemplate.css';

/**
 * Reusable template component for peptide-specific calculator pages.
 * Eliminates code duplication across Retatrutide, Tirzepatide, Semaglutide, etc.
 */
const PeptideCalculatorTemplate = ({ peptideData }) => {
  const [activeTab, setActiveTab] = useState('dosage');
  const [syringeType, setSyringeType] = useState(DEFAULT_SYRINGE_TYPE);
  const [fillLevel, setFillLevel] = useState(0);
  const [currentDose, setCurrentDose] = useState(null);
  const [quickDoseValue, setQuickDoseValue] = useState(null);

  const { seo, peptideInfo, hero, quickDoses, infoSection, guideCta, faqItems } = peptideData;

  // Handle quick dose button clicks
  const handleQuickDose = (dose) => {
    setActiveTab('dosage');
    setQuickDoseValue({
      peptideMg: quickDoses.defaultVialMg,
      bacWaterMl: quickDoses.defaultBacWaterMl,
      desiredDoseMg: dose,
    });
  };

  // Render info card items with optional bold text
  const renderInfoItem = (item, index) => {
    // Handle markdown-style bold (**text**)
    let text = item.text;
    if (text.includes('**')) {
      const parts = text.split(/\*\*(.+?)\*\*/g);
      text = parts.map((part, i) =>
        i % 2 === 1 ? <strong key={i}>{part}</strong> : part
      );
    }

    return (
      <li key={index}>
        {item.bold && <strong>{item.bold}</strong>} {text}
      </li>
    );
  };

  return (
    <div className="peptide-calculator-page">
      <SEOHead
        title={seo.title}
        description={seo.description}
        peptide={peptideInfo}
        isCalculator={true}
      />

      {/* Hero Section */}
      <div className="peptide-hero">
        <div className="hero-content">
          <Link to="/" className="back-link">
            ← Back to Main Calculator
          </Link>
          <h1 className="peptide-title">
            {hero.title}
            <span className="peptide-code">{hero.code}</span>
          </h1>
          <p className="peptide-subtitle">{hero.subtitle}</p>
          <div className="trial-badge">{hero.badge}</div>
        </div>
      </div>

      {/* Quick Dose Buttons */}
      <div className="quick-dose-section">
        <h2>{quickDoses.title}</h2>
        <div className="quick-dose-buttons">
          {quickDoses.doses.map((dose) => (
            <button
              key={dose.value}
              className="dose-btn"
              onClick={() => handleQuickDose(dose.value)}
            >
              {dose.label}
            </button>
          ))}
        </div>
      </div>

      {/* Main Calculator Section */}
      <div className="App">
        <div className="main-container">
          <div className="calculator-section">
            <div className="tab-container">
              <button
                className={`tab-button ${activeTab === 'reconstitution' ? 'active' : ''}`}
                onClick={() => setActiveTab('reconstitution')}
              >
                <span className="tab-icon">💧</span>
                Reconstitution
              </button>
              <button
                className={`tab-button ${activeTab === 'dosage' ? 'active' : ''}`}
                onClick={() => setActiveTab('dosage')}
              >
                <span className="tab-icon">💉</span>
                Dosage
              </button>
            </div>

            <div className="calculator-content">
              {activeTab === 'reconstitution' ? (
                <ReconstitutionCalculator />
              ) : (
                <DosageCalculator
                  setFillLevel={setFillLevel}
                  syringeType={syringeType}
                  setCurrentDose={setCurrentDose}
                  quickDoseValue={quickDoseValue}
                  onQuickDoseUsed={() => setQuickDoseValue(null)}
                />
              )}
            </div>
          </div>

          <div className="syringe-section">
            <div className="syringe-controls">
              <label className="syringe-select-label">Select Syringe Type:</label>
              <select
                className="syringe-select"
                value={syringeType}
                onChange={(e) => setSyringeType(e.target.value)}
              >
                {SYRINGE_OPTIONS.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            <SyringeDisplay
              fillLevel={fillLevel}
              syringeType={syringeType}
              currentDose={currentDose}
            />
          </div>
        </div>
      </div>

      {/* Information Section */}
      <div className="peptide-info-section">
        <h2>{infoSection.title}</h2>
        <div className="info-grid">
          {infoSection.cards.map((card, cardIndex) => (
            <div key={cardIndex} className="info-card">
              <h3>
                {card.icon} {card.title}
              </h3>
              {card.intro && <p>{card.intro}</p>}
              <ul>{card.items.map(renderInfoItem)}</ul>
            </div>
          ))}
        </div>
      </div>

      {/* Guide CTA Section */}
      <div className="blog-cta-wrapper">
        <div className="blog-cta-section">
          <h2>📚 {guideCta.title}</h2>
          <p>{guideCta.description}</p>
          <Link to={guideCta.linkPath} className="blog-cta-button">
            {guideCta.linkText}
          </Link>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="peptide-faq">
        <h2>Frequently Asked Questions</h2>
        <div className="faq-list">
          {faqItems.map((faq, index) => (
            <details key={index}>
              <summary>{faq.question}</summary>
              <p>{faq.answer}</p>
            </details>
          ))}
        </div>
      </div>

      {/* Footer with Links */}
      <footer className="peptide-footer">
        <div className="footer-content">
          <div className="footer-links">
            <Link to="/" className="footer-link">
              Main Calculator
            </Link>
            <Link to="/faq" className="footer-link">
              FAQ
            </Link>
            <Link to="/guides" className="footer-link">
              Guides
            </Link>
          </div>
          <p className="footer-disclaimer">
            Always consult with a healthcare professional before using any peptide
          </p>
        </div>
      </footer>
    </div>
  );
};

PeptideCalculatorTemplate.propTypes = {
  peptideData: PropTypes.shape({
    seo: PropTypes.shape({
      title: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
    }).isRequired,
    peptideInfo: PropTypes.shape({
      name: PropTypes.string.isRequired,
      brands: PropTypes.string,
      summary: PropTypes.string,
      mechanism: PropTypes.string,
      dosing: PropTypes.string,
      efficacy: PropTypes.string,
      safety: PropTypes.string,
      status: PropTypes.string,
    }).isRequired,
    hero: PropTypes.shape({
      title: PropTypes.string.isRequired,
      code: PropTypes.string.isRequired,
      subtitle: PropTypes.string.isRequired,
      badge: PropTypes.string.isRequired,
    }).isRequired,
    quickDoses: PropTypes.shape({
      title: PropTypes.string.isRequired,
      defaultVialMg: PropTypes.number.isRequired,
      defaultBacWaterMl: PropTypes.number.isRequired,
      doses: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string.isRequired,
          value: PropTypes.number.isRequired,
        })
      ).isRequired,
    }).isRequired,
    infoSection: PropTypes.shape({
      title: PropTypes.string.isRequired,
      cards: PropTypes.arrayOf(
        PropTypes.shape({
          icon: PropTypes.string.isRequired,
          title: PropTypes.string.isRequired,
          intro: PropTypes.string,
          items: PropTypes.arrayOf(
            PropTypes.shape({
              bold: PropTypes.string,
              text: PropTypes.string.isRequired,
            })
          ).isRequired,
        })
      ).isRequired,
    }).isRequired,
    guideCta: PropTypes.shape({
      title: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      linkText: PropTypes.string.isRequired,
      linkPath: PropTypes.string.isRequired,
    }).isRequired,
    faqItems: PropTypes.arrayOf(
      PropTypes.shape({
        question: PropTypes.string.isRequired,
        answer: PropTypes.string.isRequired,
      })
    ).isRequired,
  }).isRequired,
};

export default PeptideCalculatorTemplate;
